import {
  FcColor,
  FcColorGradient,
  FcColorGradientSpread,
  FcColorGradientType,
  FcColorType,
  fcBuildSvgColor
} from '@dtx-company/flowcode-generator-types/src'

export function fcGenerateGradient(
  useCmyk: boolean,
  gradient: FcColorGradient,
  name: string,
  fullCoord?: boolean,
  xstart?: number,
  xend?: number,
  ystart?: number,
  yend?: number
): string {
  if (gradient.type === FcColorGradientType.RADIAL) {
    return makeRadialGradient(
      useCmyk,
      name,
      gradient.startColor,
      gradient.endColor,
      gradient.startPercent,
      gradient.endPercent,
      gradient.color2,
      gradient.color3,
      gradient.color2Percent,
      gradient.color3Percent,
      fullCoord || false,
      gradient.spread,
      gradient.cx,
      gradient.cy,
      gradient.fx,
      gradient.fy,
      gradient.r
    )
  }

  if (!xstart) {
    xstart = 0
  }
  if (!xend) {
    xend = 100
  }
  if (!ystart) {
    ystart = 0
  }
  if (!yend) {
    yend = 100
  }

  let x1 = xstart
  let x2 = xstart
  let y1 = ystart
  let y2 = ystart
  switch (gradient.type) {
    case FcColorGradientType.LINEAR_BOTTOM:
      y1 = yend
      break
    case FcColorGradientType.LINEAR_RIGHT:
      x2 = xend
      break
    case FcColorGradientType.LINEAR_LEFT:
      x1 = xend
      break
    case FcColorGradientType.LINEAR_TOP:
      y2 = yend
      break
    case FcColorGradientType.LINEAR_BOTTOM_LEFT:
      y1 = yend
      x2 = xend
      break
    case FcColorGradientType.LINEAR_BOTTOM_RIGHT:
      x1 = xend
      y1 = yend
      break
    case FcColorGradientType.LINEAR_TOP_LEFT:
      x2 = xend
      y2 = yend
      break
    case FcColorGradientType.LINEAR_TOP_RIGHT:
      x1 = xend
      y2 = yend
      break
    default:
      throw new Error('Invalid gradient type')
  }
  return makeLinearGradient(
    useCmyk,
    name,
    x1,
    x2,
    y1,
    y2,
    gradient.startColor,
    gradient.endColor,
    gradient.startPercent,
    gradient.endPercent,
    gradient.color2,
    gradient.color3,
    gradient.color2Percent,
    gradient.color3Percent,
    fullCoord || false,
    gradient.spread
  )
}

function makeLinearGradient(
  useCmyk: boolean,
  name: string,
  x1: number,
  x2: number,
  y1: number,
  y2: number,
  startColor: FcColor,
  endColor: FcColor,
  startPercent: number,
  endPercent: number,
  color2?: FcColor,
  color3?: FcColor,
  color2Percentage?: number,
  color3Percentage?: number,
  fullCoord?: boolean,
  spread?: FcColorGradientSpread
): string {
  const units = fullCoord ? 'userSpaceOnUse' : 'objectBoundingBox'
  let str = `<defs>
             <linearGradient id="${name}" x1="${x1}%" x2="${x2}%" y1="${y1}%" y2="${y2}%" gradientUnits="${units}" spreadMethod="${getSpreadMethod(
    spread
  )}">
              <stop offset="${startPercent}%" ${fcBuildSvgColor(
    startColor,
    FcColorType.STOP,
    useCmyk
  )}/>`

  if (color2) {
    str += `<stop offset="${color2Percentage}%" ${fcBuildSvgColor(
      color2,
      FcColorType.STOP,
      useCmyk
    )}/>`
  }
  if (color3) {
    str += `<stop offset="${color3Percentage}%" ${fcBuildSvgColor(
      color3,
      FcColorType.STOP,
      useCmyk
    )}/>`
  }
  str += `<stop offset="${endPercent}%" ${fcBuildSvgColor(endColor, FcColorType.STOP, useCmyk)}/>
             </linearGradient>
            </defs>`
  return str
}

function getSpreadMethod(spread?: FcColorGradientSpread): string {
  switch (spread) {
    case FcColorGradientSpread.REFLECT:
      return 'reflect'
    case FcColorGradientSpread.REPEAT:
      return 'repeat'
    default:
      return 'pad'
  }
}

function makeRadialGradient(
  useCmyk: boolean,
  name: string,
  startColor: FcColor,
  endColor: FcColor,
  startPercent: number,
  endPercent: number,
  color2?: FcColor,
  color3?: FcColor,
  color2Percentage?: number,
  color3Percentage?: number,
  fullCoord?: boolean,
  spread?: FcColorGradientSpread,
  cx?: number,
  cy?: number,
  fx?: number,
  fy?: number,
  r?: number
): string {
  const units = fullCoord ? 'userSpaceOnUse' : 'objectBoundingBox'
  if (cx === undefined) {
    cx = 50
  }
  if (cy === undefined) {
    cy = 50
  }
  if (fx === undefined) {
    fx = 50
  }
  if (fy === undefined) {
    fy = 50
  }
  if (r === undefined) {
    r = 50
  }
  let str = `<defs>
             <radialGradient id="${name}" gradientUnits="${units}" spreadMethod="${getSpreadMethod(
    spread
  )}" cx="${cx}%"
                cy="${cy}%" fx="${fx}%" fy="${fy}%" r="${r}%">
              <stop offset="${startPercent}%" ${fcBuildSvgColor(
    startColor,
    FcColorType.STOP,
    useCmyk
  )}/>`

  if (color2) {
    str += `<stop offset="${color2Percentage}%" ${fcBuildSvgColor(
      color2,
      FcColorType.STOP,
      useCmyk
    )}/>`
  }
  if (color3) {
    str += `<stop offset="${color3Percentage}%" ${fcBuildSvgColor(
      color3,
      FcColorType.STOP,
      useCmyk
    )}/>`
  }
  str += `<stop offset="${endPercent}%" ${fcBuildSvgColor(endColor, FcColorType.STOP, useCmyk)}/>
             </radialGradient>
            </defs>`
  return str
}
